import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import { Route } from 'react-router';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import useSWR, { KeyedMutator } from 'swr';
import { APIList } from '../../../../../../../../typings/API.interface';
import { TeamIntegration, TeamIntegrationFeature, TeamIntegrationProvider } from '../../../../../../../../typings/TeamIntegration.interface';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import team_integration_schemas, { TeamIntegrationSchema } from '../../../../../configs/team-integration-schemas';
import Badge from '../../../../common/base/Badge';
import Button, { PlaceholderButton } from '../../../../common/base/Button';
import { StyledCard } from '../../../../common/base/Card';
import Icon from '../../../../common/base/Icon';
import { OverlayLoading } from '../../../../common/base/Loading';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import { useDialog } from '../../../../common/Dialog';
import CheckboxInput from '../../../../common/Form/Fields/CheckboxInput';
import SelectInput from '../../../../common/Form/Fields/SelectInput';
import TextInput from '../../../../common/Form/Fields/TextInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import Modal from '../../../../common/Modal';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import useSearchQuery from '../../../../hooks/useSearchQuery';
import useViewportBreakpoint from '../../../../hooks/useViewportBreakpoint';
import Search from '../../../../common/Search';
import { DashboardContext } from '../../DashboardContext';
import { team_channel_forms } from '../../IssueTriggers/IssueTriggersList/Forms/IssueTriggersForms';
import { showChat } from '../../../../../utils/liveChat';
import Alert from '../../../../common/base/Alert';
import RadioGroupInput from '../../../../common/Form/Fields/RadioInput';
import Container from '../../../../common/base/Container';
import Skeleton from '../../../../common/base/Skeleton';

// Set before sending the user to the provider to reconnect an existing integration in place.
const RECONNECT_KEY = 'integrations:reconnect';
const takeReconnectIntegrationId = (provider: TeamIntegrationProvider): string | null => {
  try {
    const value = JSON.parse(sessionStorage.getItem(RECONNECT_KEY) || 'null');
    sessionStorage.removeItem(RECONNECT_KEY);
    return value?.provider === provider ? value.id : null;
  } catch {
    return null;
  }
};

// Issue integrations are listed before metrics exports.
const FEATURE_ORDER: TeamIntegrationFeature[] = ['notification', 'metrics'];
const featureRank = (schema: TeamIntegrationSchema) => Math.min(...schema.features.map(feature => FEATURE_ORDER.indexOf(feature)));
const matchesSearch = (schema: TeamIntegrationSchema, search: string) => schema.label.toLowerCase().includes(search.trim().toLowerCase());

// Authorization codes are single-use, but the callback page can load more than once for
// the same code (a reload, or the app remounting). Remember the last code handled per
// provider in this tab so it is only exchanged once.
const CALLBACK_CODE_KEY = (provider: TeamIntegrationProvider) => `integrations:callback_code:${provider}`;
const isCallbackHandled = (provider: TeamIntegrationProvider, code: string) => {
  try {
    return sessionStorage.getItem(CALLBACK_CODE_KEY(provider)) === code;
  } catch {
    return false;
  }
};
const markCallbackHandled = (provider: TeamIntegrationProvider, code: string) => {
  try {
    sessionStorage.setItem(CALLBACK_CODE_KEY(provider), code);
  } catch {
    // Storage unavailable: the code is simply exchanged without the reload guard.
  }
};
const CALLBACK_WAIT_ATTEMPTS = 6;
const CALLBACK_WAIT_INTERVAL_MS = 1500;
type CallbackResult = {
  type: 'created';
  integration: TeamIntegration;
} | {
  type: 'reconnected';
} | {
  type: 'error';
  message: string;
};

// Requests exchanging an authorization code, shared by every instance of the modal.
const pending_callbacks = new Map<string, {
  reconnect: boolean;
  result: Promise<CallbackResult>;
}>();
const completeCallback = async ({
  HookdeckAPI,
  provider,
  query,
  reconnect_id,
  callback,
  callback_error,
  label
}): Promise<CallbackResult> => {
  let configs;
  try {
    configs = callback(query);
  } catch (error) {
    return {
      type: 'error',
      message: callback_error || `Failed to validate ${label} authorization, please try again!`
    };
  }
  try {
    if (reconnect_id) {
      // Reconnect in place: the integration keeps its id and issue trigger channels.
      await HookdeckAPI.team_integrations.reauthorize(reconnect_id, {
        provider,
        configs
      });
      return {
        type: 'reconnected'
      };
    }
    const integration = await HookdeckAPI.team_integrations.create({
      provider,
      configs
    });
    return {
      type: 'created',
      integration
    };
  } catch (error) {
    // Providers with a specific reason (e.g. no projects available) explain it.
    const message = error?.response?.data?.reason && error.response.message ? error.response.message : `Failed to connect ${label}, try again!`;
    return {
      type: 'error',
      message
    };
  }
};
interface IntegrationModalProps {
  integration?: TeamIntegration;
  /** Whether the project's integrations have loaded, so `integration` is known. */
  integrations_loaded: boolean;
  provider: TeamIntegrationProvider;
  onSubmit: (integration: TeamIntegration) => void;
  mutate?: () => void;
}
const IntegrationModal: React.FC<IntegrationModalProps> = t0 => {
  const $ = _c(61);
  const {
    integration,
    integrations_loaded,
    provider,
    onSubmit,
    mutate
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const history = useHistory();
  const {
    addToast
  } = useToasts();
  const [validation_error, setValidationError] = useState(null);
  const [created_integration, setCreatedIntegration] = useState(null);
  const {
    query
  } = useSearchQuery();
  const {
    label,
    fields,
    how_steps,
    callback,
    alert_banner,
    callback_error,
    confirm_callback
  } = team_integration_schemas[provider];
  const [confirmation, setConfirmation] = useState(null);
  const is_mounted = useRef(true);
  const [is_reconnecting, setIsReconnecting] = useState(false);
  const {
    validate,
    Fields: ChannelFields,
    getInitialValues
  } = team_channel_forms[provider] || {
    validate: undefined,
    Fields: undefined,
    getInitialValues: undefined
  };
  const active_integration = created_integration || integration;
  let t1;
  if ($[0] !== history) {
    t1 = () => {
      history.push({
        pathname: "/settings/project/integrations",
        state: {
          scroll: false
        }
      });
    };
    $[0] = history;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleClose = t1;
  let t2;
  if ($[2] !== HookdeckAPI.team_integrations || $[3] !== addToast || $[4] !== onSubmit || $[5] !== provider) {
    t2 = values => {
      setValidationError(null);
      const payload = provider === "prometheus" ? {
        provider,
        configs: {
          enabled: true
        }
      } : {
        provider,
        ...values
      };
      return HookdeckAPI.team_integrations.create(payload).then(async integration_0 => {
        onSubmit(integration_0);
      }).catch(e => {
        if (e.response.code === "BAD_REQUEST") {
          setValidationError(<Text danger={true}>Verification failed for provider{" "}<Text as="span" bold={true}>{team_integration_schemas[provider].label}</Text>, please check your configuration and make sure the permissions meet the minimum requirements.</Text>);
        }
        addToast("error", "Failed to create your integration, try again!");
      });
    };
    $[2] = HookdeckAPI.team_integrations;
    $[3] = addToast;
    $[4] = onSubmit;
    $[5] = provider;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleVerification = t2;
  const handleFinish = values_0 => {
    if (!values_0.attach_all) {
      if (mutate) {
        mutate();
      }
      handleClose();
      return Promise.resolve();
    }
    delete values_0.attach_all;
    let channel_values = ChannelFields ? values_0.channels[provider] : {};
    if (ChannelFields && team_channel_forms[provider]?.formatValuesToAPIInput) {
      channel_values = team_channel_forms[provider].formatValuesToAPIInput(channel_values);
    }
    return HookdeckAPI.issue_triggers.attachChannelsToAll({
      [provider]: channel_values
    }).then(async () => {
      if (mutate) {
        mutate();
      }
      handleClose();
      addToast("success", "Integration attached to all issue triggers");
    }).catch(e_0 => {
      addToast("error", "Failed to attach integration, try again!");
    });
  };
  const getModalField = _temp;
  const [is_waiting_for_integration, setIsWaitingForIntegration] = useState(false);
  const [wait_attempts, setWaitAttempts] = useState(0);
  let t3;
  if ($[7] !== addToast || $[8] !== history || $[9] !== integration || $[10] !== integrations_loaded || $[11] !== is_waiting_for_integration || $[12] !== label || $[13] !== mutate || $[14] !== wait_attempts) {
    t3 = () => {
      if (!is_waiting_for_integration || !integrations_loaded) {
        return;
      }
      if (integration) {
        setIsWaitingForIntegration(false);
        return;
      }
      if (wait_attempts < CALLBACK_WAIT_ATTEMPTS) {
        const timeout = setTimeout(() => {
          setWaitAttempts(_temp2);
          mutate?.();
        }, CALLBACK_WAIT_INTERVAL_MS);
        return () => clearTimeout(timeout);
      }
      setIsWaitingForIntegration(false);
      addToast("error", `Failed to connect ${label}, try again!`);
      history.push("/settings/project/integrations");
    };
    $[7] = addToast;
    $[8] = history;
    $[9] = integration;
    $[10] = integrations_loaded;
    $[11] = is_waiting_for_integration;
    $[12] = label;
    $[13] = mutate;
    $[14] = wait_attempts;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== integration || $[17] !== integrations_loaded || $[18] !== is_waiting_for_integration || $[19] !== wait_attempts) {
    t4 = [is_waiting_for_integration, integrations_loaded, integration, wait_attempts];
    $[16] = integration;
    $[17] = integrations_loaded;
    $[18] = is_waiting_for_integration;
    $[19] = wait_attempts;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  useEffect(t3, t4);
  let t5;
  if ($[21] !== addToast || $[22] !== handleClose || $[23] !== history || $[24] !== label || $[25] !== mutate || $[26] !== onSubmit) {
    t5 = pending => {
      if (pending.reconnect) {
        setIsReconnecting(true);
      }
      pending.result.then(result => {
        if (!is_mounted.current) {
          return;
        }
        if (result.type === "error") {
          addToast("error", result.message);
          history.push("/settings/project/integrations");
        } else {
          if (result.type === "reconnected") {
            addToast("success", `${label} reconnected`);
            mutate?.();
            handleClose();
          } else {
            setCreatedIntegration(result.integration);
            onSubmit(result.integration);
          }
        }
      });
    };
    $[21] = addToast;
    $[22] = handleClose;
    $[23] = history;
    $[24] = label;
    $[25] = mutate;
    $[26] = onSubmit;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  const attachExchange = t5;
  let t6;
  if ($[28] !== HookdeckAPI || $[29] !== attachExchange || $[30] !== callback || $[31] !== callback_error || $[32] !== label || $[33] !== provider || $[34] !== query) {
    t6 = reconnect_id => {
      markCallbackHandled(provider, query.code);
      const pending_0 = {
        reconnect: !!reconnect_id,
        result: completeCallback({
          HookdeckAPI,
          provider,
          query,
          reconnect_id,
          callback,
          callback_error,
          label
        })
      };
      pending_callbacks.set(`${provider}:${query.code}`, pending_0);
      attachExchange(pending_0);
    };
    $[28] = HookdeckAPI;
    $[29] = attachExchange;
    $[30] = callback;
    $[31] = callback_error;
    $[32] = label;
    $[33] = provider;
    $[34] = query;
    $[35] = t6;
  } else {
    t6 = $[35];
  }
  const startExchange = t6;
  let t7;
  if ($[36] !== addToast || $[37] !== attachExchange || $[38] !== callback || $[39] !== confirm_callback || $[40] !== history || $[41] !== integration || $[42] !== label || $[43] !== provider || $[44] !== query || $[45] !== startExchange) {
    t7 = () => {
      if (!callback) {
        return;
      }
      if (query.error) {
        if (!integration) {
          addToast("info", query.error_description || "Authorization was cancelled");
          history.push("/settings/project/integrations");
        }
        return;
      }
      if (!query.code) {
        if (!integration) {
          addToast("error", `Missing authorization code from ${label}`);
          history.push("/settings/project/integrations");
        }
        return;
      }
      is_mounted.current = true;
      const pending_1 = pending_callbacks.get(`${provider}:${query.code}`);
      if (pending_1) {
        attachExchange(pending_1);
      } else {
        if (isCallbackHandled(provider, query.code)) {
          setIsWaitingForIntegration(true);
        } else {
          const reconnect_id_0 = takeReconnectIntegrationId(provider);
          if (!integration || reconnect_id_0) {
            const message = reconnect_id_0 ? null : confirm_callback?.(query);
            if (message) {
              setConfirmation({
                message,
                reconnect_id: reconnect_id_0
              });
            } else {
              startExchange(reconnect_id_0);
            }
          }
        }
      }
      return () => {
        is_mounted.current = false;
      };
    };
    $[36] = addToast;
    $[37] = attachExchange;
    $[38] = callback;
    $[39] = confirm_callback;
    $[40] = history;
    $[41] = integration;
    $[42] = label;
    $[43] = provider;
    $[44] = query;
    $[45] = startExchange;
    $[46] = t7;
  } else {
    t7 = $[46];
  }
  let t8;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[47] = t8;
  } else {
    t8 = $[47];
  }
  useEffect(t7, t8);
  if (confirmation) {
    const t9 = `Connect ${label}`;
    let t10;
    if ($[48] !== confirmation.reconnect_id || $[49] !== startExchange) {
      t10 = () => {
        setConfirmation(null);
        startExchange(confirmation.reconnect_id);
      };
      $[48] = confirmation.reconnect_id;
      $[49] = startExchange;
      $[50] = t10;
    } else {
      t10 = $[50];
    }
    let t11;
    if ($[51] !== confirmation.message) {
      t11 = <Text as="p" m={0}>{confirmation.message}</Text>;
      $[51] = confirmation.message;
      $[52] = t11;
    } else {
      t11 = $[52];
    }
    let t12;
    if ($[53] !== handleClose || $[54] !== t10 || $[55] !== t11 || $[56] !== t9) {
      t12 = <Modal title={t9} submit_label="Connect" cancel_label="Cancel" onSubmit={t10} onCancel={handleClose} onClose={handleClose}>{t11}</Modal>;
      $[53] = handleClose;
      $[54] = t10;
      $[55] = t11;
      $[56] = t9;
      $[57] = t12;
    } else {
      t12 = $[57];
    }
    return t12;
  }
  if (!active_integration && callback || is_reconnecting || is_waiting_for_integration) {
    let t9;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <OverlayLoading />;
      $[58] = t9;
    } else {
      t9 = $[58];
    }
    return t9;
  }
  const t9 = active_integration ? active_integration.features.includes("notification") ? <Formik enableReinitialize={true} initialValues={{
    attach_all: false,
    channels: getInitialValues ? {
      [provider]: getInitialValues({})
    } : {}
  }} validate={values_1 => {
    if (!values_1?.attach_all || !ChannelFields || !validate) {
      return;
    }
    if (!values_1?.channels || !values_1.channels[provider]) {
      return;
    }
    return validate(values_1.channels[provider]);
  }} onSubmit={handleFinish}>{props => <Form><Modal title={`${label} Connected`} submit_label={props.values.attach_all ? "Confirm" : "Skip"} cancel_label={undefined} onSubmit={props.handleSubmit} onClose={handleClose} is_submitting={props.isSubmitting}><Text semi={true} as="p">Attach to Issue Triggers</Text><CheckboxInput m={{
          b: 4
        }} name="attach_all" label="Attach to all issue triggers" help="Optionally attach this integration to all issue triggers now, or configure individually later." />{props.values.attach_all && ChannelFields && <><Text semi={true} as="p">Configuration</Text>{alert_banner && <Alert info={true} inline={true} m={{
            b: 4
          }}>{alert_banner}</Alert>}{ChannelFields(active_integration)}</>}</Modal></Form>}</Formik> : null : <Formik initialValues={{
    attach_all: true
  }} onSubmit={handleVerification}>{props_0 => <Form><Modal title={`Connect ${label}`} submit_label="Connect" cancel_label="Cancel" onSubmit={props_0.handleSubmit} onCancel={handleClose} onClose={handleClose} is_submitting={props_0.isSubmitting}>{how_steps && <><Text semi={true} as="p">Instructions</Text><ol>{how_steps?.map(_temp3)}</ol></>}{fields && fields.length > 0 && <><Text semi={true} as="p">Configuration</Text>{provider && fields?.map(field_0 => getModalField(field_0))}<Text as="p" muted={true}>All secrets are AES encrypted.</Text></>}{validation_error}</Modal></Form>}</Formik>;
  let t10;
  if ($[59] !== t9) {
    t10 = <>{t9}</>;
    $[59] = t9;
    $[60] = t10;
  } else {
    t10 = $[60];
  }
  return t10;
};
const IntegrationCard: React.FC<{
  provider: TeamIntegrationProvider;
  schema: TeamIntegrationSchema;
  integration?: TeamIntegration;
  mutate?: KeyedMutator<APIList<TeamIntegration>>;
}> = ({
  provider,
  schema,
  integration,
  mutate
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription
  } = useContext(DashboardContext);
  const history = useHistory();
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  let allow = false;
  if (subscription!.features?.includes('all_integrations') && schema?.features.includes('notification')) {
    allow = true;
  }
  if (subscription!.features?.includes('metrics_export')) {
    allow = true;
  }
  const button_action = schema.fields ? {
    as: RouterLink,
    to: location => ({
      ...location,
      pathname: `/settings/project/integrations/${provider}/connect`,
      state: {
        scroll: false
      }
    })
  } : {
    onClick: () => {
      schema.onClick?.();
    }
  };
  const disconnect = async (id: string) => {
    try {
      const {
        provider_cleanup_failed
      } = await HookdeckAPI.team_integrations.delete(id);
      // Return a new list: mutating the cached one in place doesn't re-render.
      mutate?.(integrations => integrations ? {
        ...integrations,
        count: integrations.count - 1,
        models: integrations.models.filter(m => m.id !== id)
      } : integrations);
      if (provider_cleanup_failed && schema?.cleanup_failed_message) {
        addToast('warning', schema.cleanup_failed_message);
      } else {
        addToast('success', 'Integration disconnected');
      }
    } catch (err) {
      addToast('error', err.message);
    }
  };
  const handleDisconnect = (id_0: string, provider_0: string) => {
    showDialog(() => disconnect(id_0), () => null, {
      title: `Disconnect ${team_integration_schemas[provider_0]?.label}`,
      message: <Text>
            Are you sure you want to disconnect{' '}
            <Text as={'span'} bold>
              {team_integration_schemas[provider_0]?.label}
            </Text>
            ? You will no longer receive notifications for this integration.
          </Text>,
      submit_label: 'Disconnect',
      cancel_label: 'Cancel',
      danger: true
    });
  };
  const is_expired = integration?.expired === true;
  // Providers that keep working with cached data while temporarily unreachable (e.g. Sentry).
  const is_degraded = !is_expired && (integration?.configs as any)?.status === 'degraded';
  const account_name = (integration?.configs as any)?.organization_name as string | undefined;
  const handleReconnect = async (id_1: string, provider_1: TeamIntegrationProvider) => {
    if (schema?.supports_reauthorize) {
      // Re-authorize in place: deleting first would remove it from every issue trigger.
      sessionStorage.setItem(RECONNECT_KEY, JSON.stringify({
        provider: provider_1,
        id: id_1
      }));
      button_action.onClick?.();
      return;
    }
    try {
      await HookdeckAPI.team_integrations.delete(id_1);
      mutate?.();
      if (schema?.fields) {
        history.push(`/settings/project/integrations/${provider_1}/connect`);
      } else {
        button_action.onClick?.();
      }
    } catch (err_0) {
      addToast('error', err_0.message);
    }
  };
  return <StyledCard p={{
    x: 4,
    y: 3
  }} h={{
    pc: 100
  }} flex={{
    direction: 'column',
    justify: 'center'
  }}>
      <Div flex={{
      justify: 'space-between',
      align: 'center',
      gap: 3
    }}>
        <Text semi as={'div'} m={0} flex={{
        align: 'center',
        gap: 2,
        wrap: true
      }}>
          <Icon icon={schema.icon} small />
          {schema.label}
          {integration && !is_expired && !is_degraded && <Badge subtle success>
              Connected
            </Badge>}
          {is_degraded && <Tooltip tooltip={`Hookdeck can't reach ${schema.label} right now. Your setup is kept; if this lasts, issues may not reach ${schema.label}.`}>
              <Badge subtle warning>
                Degraded
              </Badge>
            </Tooltip>}
          {is_expired && <Tooltip tooltip={schema?.supports_reauthorize ? `${schema.label} no longer accepts Hookdeck's access. Reconnect to restore it: your issue trigger settings are kept.` : 'This connection has expired. Please reconnect to restore functionality.'}>
              <Badge subtle danger>
                Expired
              </Badge>
            </Tooltip>}
          {integration && account_name && <Tooltip tooltip={`Connected to ${account_name}`}>
              <Icon icon="info" muted small pointer />
            </Tooltip>}
        </Text>
        {integration ? is_expired ? <Div flex={{
        gap: 2
      }}>
              <Button.Permission onClick={() => handleReconnect(integration.id, integration.provider)} outline danger icon="disconnect">
                Reconnect
              </Button.Permission>
            </Div> : <Button.Permission onClick={() => handleDisconnect(integration.id, integration.provider)} outline danger icon="disconnect">
              Disconnect
            </Button.Permission> : !allow ? <Tooltip tooltip="Upgrade plan to enable integrations." side="bottom" align="left" cta={{
        icon: 'upgrade',
        label: 'Upgrade',
        to: '/settings/organization/plans?highlight=all_integrations'
      }}>
            <Button as={'div'} disabled outline icon="connect">
              Connect
            </Button>
          </Tooltip> : <Button.Permission {...button_action} outline icon="connect">
            Connect
          </Button.Permission>}
      </Div>
    </StyledCard>;
};
const ConfigureStepModal: React.FC<{
  provider: TeamIntegrationProvider;
}> = t0 => {
  const $ = _c(17);
  const {
    provider
  } = t0;
  const history = useHistory();
  const schema = team_integration_schemas[provider];
  const {
    query
  } = useSearchQuery();
  let t1;
  if ($[0] !== query || $[1] !== schema.configure_step) {
    t1 = () => {
      if (schema.configure_step.callback) {
        schema.configure_step.callback(query);
      }
    };
    $[0] = query;
    $[1] = schema.configure_step;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      success: true
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== history) {
    t4 = () => {
      history.push("/settings/project/integrations", {
        scroll: false
      });
    };
    $[5] = history;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== schema.configure_step.description) {
    t5 = <Text semi={true} as="p">{schema.configure_step.description}</Text>;
    $[7] = schema.configure_step.description;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== schema.configure_step.button_href || $[10] !== schema.configure_step.button_text) {
    t6 = <Button as="a" icon="link" href={schema.configure_step.button_href}>{schema.configure_step.button_text}</Button>;
    $[9] = schema.configure_step.button_href;
    $[10] = schema.configure_step.button_text;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== schema.configure_step.label || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <Modal title={schema.configure_step.label} icon_theme={t3} onClose={t4}>{t5}{t6}</Modal>;
    $[12] = schema.configure_step.label;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
const TeamIntegrations: React.FC = () => {
  const $ = _c(41);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = APIMethodKeys.team_integrations.list();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== HookdeckAPI.team_integrations) {
    t1 = () => HookdeckAPI.team_integrations.list();
    $[1] = HookdeckAPI.team_integrations;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: integrations,
    mutate
  } = useSWR(t0, t1);
  const [search, setSearch] = useState("");
  const breakpoint = useViewportBreakpoint();
  const columns = breakpoint === "mobile" ? 1 : 2;
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[3] !== addToast || $[4] !== columns || $[5] !== integrations || $[6] !== mutate || $[7] !== search) {
    const connected_integrations = integrations?.models.filter(integration => matchesSearch(team_integration_schemas[integration.provider], search)) || [];
    const not_connected_integrations = Object.entries(team_integration_schemas).filter(t9 => {
      const [key, schema] = t9;
      return (integrations?.models.findIndex(i => i.provider === key) ?? -1) < 0 && matchesSearch(schema, search);
    });
    const cards = [...connected_integrations.map(_temp5), ...not_connected_integrations.map(_temp6)].sort(_temp7);
    let t10;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <PlaceholderButton.Permission p={{
        x: 4,
        y: 4
      }} h={{
        pc: 100
      }} onClick={showChat}>Request an integration</PlaceholderButton.Permission>;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    const request_integration = t10;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Route path={["/settings/project/integrations/:provider/configure"]} render={_temp8} />;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t11;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = ["/settings/project/integrations/:provider/connect"];
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    if ($[19] !== addToast || $[20] !== integrations || $[21] !== mutate) {
      t8 = <Route path={t11} render={t12 => {
        const {
          match: match_0
        } = t12;
        return <IntegrationModal integration={integrations?.models.find(i_0 => i_0.provider === match_0.params.provider)} integrations_loaded={!!integrations} provider={match_0.params.provider as TeamIntegrationProvider} onSubmit={integration_1 => {
          mutate(current => current && {
            ...current,
            count: current.count + 1,
            models: [integration_1, ...current.models.filter(i_1 => i_1.id !== integration_1.id)]
          }, {
            revalidate: true
          });
          addToast("success", "Integration connected");
        }} mutate={() => mutate()} />;
      }} />;
      $[19] = addToast;
      $[20] = integrations;
      $[21] = mutate;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    T0 = Container;
    t2 = true;
    t3 = true;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        b: 10
      };
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    let t12;
    let t13;
    let t14;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        justify: "space-between",
        align: "center",
        gap: 4,
        wrap: true
      };
      t13 = {
        b: 4
      };
      t14 = <Text bold={true} size="l" as="h2" m={0}>Integrations</Text>;
      $[24] = t12;
      $[25] = t13;
      $[26] = t14;
    } else {
      t12 = $[24];
      t13 = $[25];
      t14 = $[26];
    }
    let t15;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = {
        px: 280
      };
      $[27] = t15;
    } else {
      t15 = $[27];
    }
    if ($[28] !== search) {
      t5 = <Div flex={t12} m={t13}>{t14}<Search w={t15} small={true} name="integration_search" placeholder="Search integrations..." value={search} onChange={setSearch} /></Div>;
      $[28] = search;
      $[29] = t5;
    } else {
      t5 = $[29];
    }
    t6 = !integrations ? <Div grid={{
      columns,
      gap: 4
    }}>{Object.entries(team_integration_schemas).map(_temp9)}</Div> : <Div flex={{
      direction: "column",
      gap: 4
    }}>{cards.length === 0 && <Text muted={true} as="p" m={0}>{`No integrations match "${search.trim()}".`}</Text>}<Div grid={{
        columns,
        gap: 4
      }}>{cards.map(card => <IntegrationCard key={card.key} provider={card.provider} schema={card.schema} integration={card.integration} mutate={card.integration ? mutate : undefined} />)}{request_integration}</Div></Div>;
    $[3] = addToast;
    $[4] = columns;
    $[5] = integrations;
    $[6] = mutate;
    $[7] = search;
    $[8] = T0;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    T0 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
  }
  let t9;
  if ($[30] !== T0 || $[31] !== t2 || $[32] !== t3 || $[33] !== t4 || $[34] !== t5 || $[35] !== t6) {
    t9 = <T0 large={t2} left={t3} m={t4}>{t5}{t6}</T0>;
    $[30] = T0;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = t6;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t10 = <>{t7}{t8}{t9}</>;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  return t10;
};
export default TeamIntegrations;
function _temp(field) {
  const {
    type,
    name,
    label: label_0,
    options,
    required,
    placeholder,
    ...rest
  } = field;
  switch (type) {
    case "select":
      {
        return <SelectInput block={true} key={name} label={label_0} name={`configs.${name}`} options={options} required={required === false ? false : true} {...rest} />;
      }
    case "radio":
      {
        return <Div m={{
          bottom: 4
        }}><RadioGroupInput key={name} label={label_0} name={`configs.${name}`} options={options} required={required === false ? false : true} {...rest} /></Div>;
      }
    default:
      {
        return <TextInput key={name} label={label_0} type={type} placeholder={placeholder} default_value="" name={`configs.${name}`} required={required === false ? false : true} {...rest} />;
      }
  }
}
function _temp2(attempts) {
  return attempts + 1;
}
function _temp3(step, i) {
  return <li key={i} dangerouslySetInnerHTML={{
    __html: step
  }} />;
}
function _temp5(integration_0) {
  return {
    key: integration_0.id,
    provider: integration_0.provider,
    schema: team_integration_schemas[integration_0.provider],
    integration: integration_0,
    is_connected: true
  };
}
function _temp6(t0) {
  const [key_0, schema_0] = t0;
  return {
    key: key_0,
    provider: key_0 as TeamIntegrationProvider,
    schema: schema_0,
    integration: undefined,
    is_connected: false
  };
}
function _temp7(a, b) {
  return featureRank(a.schema) - featureRank(b.schema) || Number(b.is_connected) - Number(a.is_connected);
}
function _temp8(t0) {
  const {
    match
  } = t0;
  return <ConfigureStepModal provider={match.params.provider as TeamIntegrationProvider} />;
}
function _temp9(t0) {
  const [key_1] = t0;
  return <Skeleton key={key_1} h={{
    px: 72
  }} w={100} />;
}